import React, { PropsWithChildren, useMemo } from "react";
import { StyleProp, StyleSheet, View, ViewStyle, useWindowDimensions } from "react-native";
import Carousel from "../../components/Carousel";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";
import MarkdownView from "../MarkdownView";
import LogoImage from "../../assets/images/logo.svg";
import WelcomeBubbleSvg from "../../assets/images/welcome-bubble.svg";
import WelcomeLineSvg from "../../assets/images/welcome-line.svg";
import WelcomeEndBubbleSvg from "../../assets/images/welcome-end-bubble.svg";
import LiftDiagramSvg from "../../assets/images/lift-diagram.svg";
import { BodyMediumText, TitleLargeText } from "../FormattedText";
import { CarouselElementType } from "../CarouselElement";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import AutoSizeImage from "../AutoSizeImage";
import VStack from "../../ui_atoms/VStack";
import Text from "../Text";
import Gap from "../../ui_atoms/Gap";
import { Rectangle } from "./components/Rectangle";
import { CheckItem } from "./components/CheckItem";

type WelcomeCarouselProps = {
    scrollViewBottomPadding?: number;
};

const PARTNER_LOGO_HEIGHT = 24;

const TitleTextComponent = ({ children }: { children: string }) => {
    const { width: windowWidth } = useWindowDimensions();
    const { gridSize, colors } = useTheme();
    return (
        <MarkdownView
            allowFontScaling={false}
            style={{ width: windowWidth - gridSize * 2 }}
            markdown={children}
            fontColor={colors.secondary}
        />
    );
};

const CircleNumberTextComponent = ({ children }: PropsWithChildren) => {
    const styles = useStyles();
    return (
        <TitleLargeText style={styles.circleNumberText} allowFontScaling={false}>
            {children}
        </TitleLargeText>
    );
};

const DetailTextComponent = ({ children }: PropsWithChildren) => {
    const { width: windowWidth } = useWindowDimensions();
    return (
        <BodyMediumText style={{ width: windowWidth * 0.5 }} allowFontScaling={false}>
            {children}
        </BodyMediumText>
    );
};

type OurPartnersComponentProps = {
    style?: StyleProp<ViewStyle>;
};

const OurPartnersComponent = ({ style }: OurPartnersComponentProps) => {
    const styles = useStyles();
    const i18n = useI18n();

    return (
        <VStack gap="2/3" style={[styles.ourPartnersContainer, style]}>
            <Text style={styles.ourPartnersText}>
                {i18n.t("screens.auth.presentation_carousel.our_partners")}
            </Text>
            <View style={styles.ourPartnersLogos}>
                <AutoSizeImage
                    style={{ transform: [{ translateY: -3.5 }] }}
                    source={require("../../assets/images/centre_patronal.png")}
                    fixedHeight={PARTNER_LOGO_HEIGHT}
                />
                <AutoSizeImage
                    source={require("../../assets/images/suva.png")}
                    fixedHeight={PARTNER_LOGO_HEIGHT}
                    scale={0.6}
                />
                <AutoSizeImage
                    source={require("../../assets/images/groupe_mutuel.png")}
                    fixedHeight={PARTNER_LOGO_HEIGHT}
                />
                <AutoSizeImage
                    source={require("../../assets/images/gastrosocial.png")}
                    fixedHeight={PARTNER_LOGO_HEIGHT}
                    scale={0.8}
                />
            </View>
        </VStack>
    );
};

export const WelcomeCarousel = ({ scrollViewBottomPadding = 0 }: WelcomeCarouselProps) => {
    const styles = useStyles();
    const { colors, gridSize } = useTheme();
    const i18n = useI18n();
    const insets = useSafeAreaInsets();
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();
    const SCALE_1 = 1.1;
    const SCALE_2 = 1.2;

    const mainColor = colors.secondary;

    const CIRCLE = useMemo(() => <View style={styles.circle} />, [styles.circle]);

    const STEP_2_CIRCLE_POSITIONS = useMemo(() => {
        return [
            {
                top: insets.top + windowHeight * 0.25,
                left: gridSize + 40,
            },
            {
                top: insets.top + windowHeight * 0.4,
                left: gridSize + 5,
            },
            {
                top: insets.top + windowHeight * 0.55,
                left: gridSize + 40,
            },
        ];
    }, [insets.top, windowHeight, gridSize]);

    const DETAIL_TEXT_MARGIN = CIRCLE_SIZE + 20;

    const ELEMENTS = useMemo<CarouselElementType[]>(
        () =>
            [
                // step 0
                {
                    stepIndex: 1,
                    scrollScale: 1,
                    style: {
                        bottom: -70,
                        left: 0,
                        justifySelf: "flex-start",
                    },
                    component: (
                        <WelcomeLineSvg
                            width={windowWidth * 4 + 140}
                            style={{
                                bottom: 0,
                                position: "absolute",
                            }}
                        />
                    ),
                },
                {
                    stepIndex: 0,
                    scrollScale: 1,
                    style: {
                        top: 0,
                        left: 0,
                    },
                    component: (
                        <View style={{ width: windowWidth, height: windowHeight * 0.55 }}>
                            <WelcomeBubbleSvg
                                width="100%"
                                style={{ position: "absolute", bottom: 0, opacity: 1 }}
                            />
                            <View style={[styles.introContainer, { top: windowHeight * 0.12 }]}>
                                <LogoImage height={76.24} width={104.24} />
                                <Gap gap={2.5} />
                                <TitleLargeText style={styles.introText}>
                                    {i18n.t("screens.auth.presentation_carousel.step_0.title")}
                                </TitleLargeText>
                            </View>
                        </View>
                    ),
                },
                {
                    stepIndex: 0,
                    scrollScale: 1,
                    style: {
                        bottom: scrollViewBottomPadding,
                        left: 0,
                        alignItems: "center" as const,
                        width: "100%" as const,
                    },
                    component: <OurPartnersComponent style={{ position: "absolute" }} />,
                },
                // step 1
                {
                    stepIndex: 1,
                    scrollScale: SCALE_1,
                    style: {
                        left: gridSize,
                        top: insets.top + gridSize + 20,
                    },
                    component: (
                        <MarkdownView
                            allowFontScaling={false}
                            style={{ width: windowWidth - gridSize * 2 }}
                            markdownStyle={{ heading1: { textAlign: "start" } }}
                            markdown={i18n.t("screens.auth.presentation_carousel.step_1.title")}
                            fontColor={mainColor}
                        />
                    ),
                },
                // step 2
                {
                    stepIndex: 2,
                    scrollScale: SCALE_1,
                    style: {
                        top: insets.top + gridSize,
                        left: gridSize,
                    },
                    component: (
                        <TitleTextComponent>
                            {i18n.t("screens.auth.presentation_carousel.step_2.title")}
                        </TitleTextComponent>
                    ),
                },
                {
                    stepIndex: 2,
                    scrollScale: SCALE_1,
                    style: STEP_2_CIRCLE_POSITIONS[0],
                    component: CIRCLE,
                },
                {
                    stepIndex: 2,
                    scrollScale: SCALE_2,
                    style: {
                        top: STEP_2_CIRCLE_POSITIONS[0].top + 11,
                        left: STEP_2_CIRCLE_POSITIONS[0].left + 16,
                    },
                    component: <CircleNumberTextComponent>1</CircleNumberTextComponent>,
                },
                {
                    stepIndex: 2,
                    scrollScale: SCALE_2,
                    style: {
                        ...STEP_2_CIRCLE_POSITIONS[0],
                        left: STEP_2_CIRCLE_POSITIONS[0].left + DETAIL_TEXT_MARGIN,
                    },
                    component: (
                        <DetailTextComponent>
                            {i18n.t("screens.auth.presentation_carousel.step_2.detail_1")}
                        </DetailTextComponent>
                    ),
                },
                {
                    stepIndex: 2,
                    scrollScale: SCALE_1,
                    style: STEP_2_CIRCLE_POSITIONS[1],
                    component: CIRCLE,
                },
                {
                    stepIndex: 2,
                    scrollScale: SCALE_2,
                    style: {
                        top: STEP_2_CIRCLE_POSITIONS[1].top + 10,
                        left: STEP_2_CIRCLE_POSITIONS[1].left + 15,
                    },
                    component: <CircleNumberTextComponent>2</CircleNumberTextComponent>,
                },
                {
                    stepIndex: 2,
                    scrollScale: SCALE_2,
                    style: {
                        ...STEP_2_CIRCLE_POSITIONS[1],
                        left: STEP_2_CIRCLE_POSITIONS[1].left + DETAIL_TEXT_MARGIN,
                    },
                    component: (
                        <DetailTextComponent>
                            {i18n.t("screens.auth.presentation_carousel.step_2.detail_2")}
                        </DetailTextComponent>
                    ),
                },
                {
                    stepIndex: 2,
                    scrollScale: SCALE_1,
                    style: STEP_2_CIRCLE_POSITIONS[2],

                    component: CIRCLE,
                },
                {
                    stepIndex: 2,
                    scrollScale: SCALE_2,
                    style: {
                        top: STEP_2_CIRCLE_POSITIONS[2].top + 2,
                        left: STEP_2_CIRCLE_POSITIONS[2].left + 11,
                    },
                    component: <CircleNumberTextComponent>3</CircleNumberTextComponent>,
                },
                {
                    stepIndex: 2,
                    scrollScale: SCALE_2,
                    style: {
                        ...STEP_2_CIRCLE_POSITIONS[2],
                        left: STEP_2_CIRCLE_POSITIONS[2].left + DETAIL_TEXT_MARGIN,
                    },
                    component: (
                        <DetailTextComponent>
                            {i18n.t("screens.auth.presentation_carousel.step_2.detail_3")}
                        </DetailTextComponent>
                    ),
                },
                // step 3
                {
                    stepIndex: 3,
                    scrollScale: SCALE_1,
                    style: {
                        top: insets.top + gridSize,
                        left: gridSize,
                    },
                    component: (
                        <TitleTextComponent>
                            {i18n.t("screens.auth.presentation_carousel.step_3.title")}
                        </TitleTextComponent>
                    ),
                },
                {
                    stepIndex: 3,
                    scrollScale: SCALE_2,
                    style: {
                        height: "100%" as const,
                        width: "100%" as const,
                        paddingTop: 100,
                        paddingHorizontal: gridSize,
                        paddingBottom: 20,
                        alignItems: "center" as const,
                        justifyContent: "space-evenly" as const,
                    },
                    component: (
                        <>
                            <Rectangle
                                icons={["hotel"]}
                                title={i18n.t("screens.auth.presentation_carousel.step_3.clients")}
                                description={
                                    <BodyMediumText numberOfLines={3} allowFontScaling={false}>
                                        {i18n.t(
                                            "screens.auth.presentation_carousel.step_3.clients_description",
                                        )}
                                    </BodyMediumText>
                                }
                            />
                            <Rectangle
                                icons={["user-female", "user-male"]}
                                title={i18n.t("screens.auth.presentation_carousel.step_3.workers")}
                                description={
                                    <BodyMediumText numberOfLines={3} allowFontScaling={false}>
                                        {i18n.t(
                                            "screens.auth.presentation_carousel.step_3.workers_description",
                                        )}
                                    </BodyMediumText>
                                }
                            />
                        </>
                    ),
                },
                // step 4
                {
                    stepIndex: 4,
                    scrollScale: SCALE_1,
                    style: {
                        top: insets.top + gridSize,
                        left: gridSize,
                    },
                    component: (
                        <TitleTextComponent>
                            {i18n.t("screens.auth.presentation_carousel.step_4.title")}
                        </TitleTextComponent>
                    ),
                },
                {
                    stepIndex: 4,
                    scrollScale: SCALE_2,
                    style: {
                        height: "100%" as const,
                        width: "100%" as const,
                        paddingTop: 100,
                        paddingHorizontal: gridSize,
                        paddingBottom: 20,
                        alignItems: "center" as const,
                        justifyContent: "space-evenly" as const,
                    },
                    component: (
                        <>
                            <Rectangle
                                description={
                                    <View
                                        style={{
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            height: "100%",
                                            flex: 1,
                                        }}
                                    >
                                        <CheckItem
                                            markdown={i18n.t(
                                                "screens.auth.presentation_carousel.step_4.detail_1",
                                            )}
                                        />
                                        <CheckItem
                                            markdown={i18n.t(
                                                "screens.auth.presentation_carousel.step_4.detail_2",
                                            )}
                                        />
                                        <CheckItem
                                            markdown={i18n.t(
                                                "screens.auth.presentation_carousel.step_4.detail_3",
                                            )}
                                        />
                                    </View>
                                }
                            />
                            <LiftDiagramSvg width="100%" />
                        </>
                    ),
                },
                // step 5,
                {
                    stepIndex: 5,
                    scrollScale: 1,
                    style: {
                        top: 80,
                        left: 0,
                        width: "100%" as const,
                        alignItems: "center" as const,
                    },
                    component: <LogoImage height={76.24} width={104.24} />,
                },
                {
                    stepIndex: 5,
                    scrollScale: 1,
                    style: {
                        bottom: 0,
                        left: 0,
                        paddingBottom: -scrollViewBottomPadding,
                        zIndex: -2,
                    },
                    component: (
                        <View
                            style={{
                                width: windowWidth,
                                height: windowHeight * 0.7,
                                overflow: "visible",
                            }}
                        >
                            <WelcomeEndBubbleSvg
                                width="100%"
                                style={{ position: "absolute", top: 0 }}
                            />
                            <View
                                style={[
                                    styles.outroContainer,
                                    { paddingBottom: scrollViewBottomPadding + 70 },
                                ]}
                            >
                                <TitleLargeText style={styles.introText}>
                                    {i18n.t("screens.auth.presentation_carousel.step_5.title")}
                                </TitleLargeText>
                                <OurPartnersComponent />
                            </View>
                        </View>
                    ),
                },
            ].map((element) => ({
                ...element,
                style: {
                    ...(element?.style || {}),
                    paddingBottom: (element?.style?.paddingBottom || 0) + scrollViewBottomPadding,
                },
            })),
        [
            windowWidth,
            windowHeight,
            styles.introContainer,
            styles.introText,
            styles.outroContainer,
            i18n,
            scrollViewBottomPadding,
            gridSize,
            insets.top,
            mainColor,
            STEP_2_CIRCLE_POSITIONS,
            CIRCLE,
            DETAIL_TEXT_MARGIN,
        ],
    );

    const steps = useMemo(
        () =>
            new Array(new Set(ELEMENTS.map((item) => item.stepIndex)).size).map((_, index) => (
                <View key={index} />
            )),
        [ELEMENTS],
    );

    return (
        <Carousel
            loop={false}
            dotColor={colors.secondary}
            overscrollEnabled={false}
            dotsOffset={scrollViewBottomPadding}
            elements={ELEMENTS}
        >
            {steps}
        </Carousel>
    );
};

const CIRCLE_SIZE = 46;

const useStyles = () => {
    const { gridSize, fonts, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                circle: {
                    width: CIRCLE_SIZE,
                    height: CIRCLE_SIZE,
                    borderRadius: CIRCLE_SIZE / 2,
                    backgroundColor: colors.onPrimary,
                },
                floatingText: {
                    position: "absolute",
                },
                circleNumberText: {
                    fontSize: 44,
                    color: colors.secondary,
                },
                introContainer: {
                    width: "100%",
                    alignItems: "center",
                },
                introText: {
                    textAlign: "center",
                    color: colors.secondary,
                },
                outroContainer: {
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingTop: gridSize * 3,
                    flex: 1,
                },
                ourPartnersContainer: {
                    width: "100%",
                    alignItems: "center",
                },
                ourPartnersText: {
                    ...fonts.bodySmall,
                    color: colors.primary,
                },
                ourPartnersLogos: {
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    paddingHorizontal: gridSize / 2,
                },
            }),
        [colors.onPrimary, colors.primary, colors.secondary, fonts.bodySmall, gridSize],
    );
};
